/**
 * Permission helpers — forked VERBATIM from vCIP_droom_4.0/src/media/useMediaPermissions.ts
 * (the exported detectors + recovery copy; the LiveKit-oriented hook itself is not carried over —
 * svapp's camera machine is media/useCaptureCamera.ts). Deck keys renamed `droom.*` → `sv.*`.
 *
 * UA heuristics are best-effort and never load-bearing for security: they route the representative
 * to honest recovery copy (no site can re-open a denied prompt; in-app webviews cannot grant the
 * camera), nothing more.
 */

export type BrowserFamily = 'chrome' | 'edge' | 'safari' | 'firefox' | 'samsung' | 'other';

export function detectBrowser(ua: string): BrowserFamily {
  if (/EdgA?\//.test(ua)) return 'edge';
  if (/SamsungBrowser/.test(ua)) return 'samsung';
  if (/Firefox|FxiOS/.test(ua)) return 'firefox';
  // Chrome check must precede Safari (Chrome UA also contains "Safari").
  if (/Chrome|CriOS|Chromium/.test(ua)) return 'chrome';
  if (/Safari/.test(ua)) return 'safari';
  return 'other';
}

/**
 * In-app webview detection. These containers routinely cannot grant camera/mic (or silently block
 * them), so we send the representative to a real browser rather than into a prompt that will never
 * work. Invites arrive over WhatsApp/SMS — this is the case that matters.
 */
export function detectWebview(ua: string): boolean {
  if (/FBAN|FBAV|FB_IAB|Instagram|Line\/|Twitter|MicroMessenger|WhatsApp/i.test(ua)) return true;
  if (/; wv\)/.test(ua)) return true;
  const iOS = /iPhone|iPad|iPod/.test(ua);
  if (iOS && /AppleWebKit/.test(ua) && !/Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)) {
    return true;
  }
  return false;
}

/** The OS the recovery card illustrates (SPEC_CAPTURE_UX_R2 §7.4) — detected the way the iOS motion gate already is. */
export type Platform = 'android' | 'ios' | 'other';

export function detectPlatform(ua: string): Platform {
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

/**
 * F-21 — the iPhone browser in hand, as iOS Settings names it. Every iOS browser has its own entry (Settings → Chrome →
 * Camera; Location Services → Chrome); only Safari's location entry is called "Safari Websites" (`iosLocationEntry`).
 * Null when it cannot be told (an app's built-in browser): the steps then say "your browser app".
 */
export type IosBrowserApp = 'Safari' | 'Chrome' | 'Firefox' | 'Edge' | 'Opera';

export function iosBrowserApp(ua: string): IosBrowserApp | null {
  if (/CriOS\//.test(ua)) return 'Chrome';
  if (/FxiOS\//.test(ua)) return 'Firefox';
  if (/EdgiOS\//.test(ua)) return 'Edge';
  if (/OPiOS\/|OPT\//.test(ua)) return 'Opera';
  if (/Version\/[\d.]+.*Safari\//.test(ua)) return 'Safari';
  return null;
}

/** The browser's entry under Location Services (Safari's is "Safari Websites"), or null when unknown. */
export function iosLocationEntry(ua: string): string | null {
  const app = iosBrowserApp(ua);
  return app === 'Safari' ? 'Safari Websites' : app;
}

/** Location recovery steps by platform — a path in words (iOS permits no deep link), never a guess at a menu that is not there. */
export function locationRecoveryFor(platform: Platform): string[] {
  switch (platform) {
    case 'android':
      return ['sv.perm.loc.android.1', 'sv.perm.loc.android.2', 'sv.perm.loc.android.3'];
    case 'ios':
      return ['sv.perm.loc.ios.1', 'sv.perm.loc.ios.2', 'sv.perm.loc.ios.3'];
    default:
      return ['sv.perm.loc.other.1', 'sv.perm.loc.other.2', 'sv.perm.loc.other.3'];
  }
}

/**
 * US-03482 P1-3 — "Location off" (§16i: POSITION_UNAVAILABLE, or code 1 while the site permission reads `granted` —
 * blocked below the browser). No web page can switch the device's location on, so the customer is told where every
 * switch is, in words: the device's Location (Android quick settings; iOS Location Services) AND the browser app's own
 * location permission (Android: Settings → Apps → the browser; iOS: the browser's entry under Location Services).
 */
export function locationOffStepsFor(platform: Platform): string[] {
  switch (platform) {
    case 'android':
      return ['sv.geo.gate.off.android.1', 'sv.geo.gate.off.android.2', 'sv.geo.gate.off.android.app', 'sv.geo.gate.off.android.3'];
    case 'ios':
      return ['sv.geo.gate.off.ios.1', 'sv.geo.gate.off.ios.2', 'sv.geo.gate.off.ios.app', 'sv.geo.gate.off.ios.3'];
    default:
      return ['sv.geo.gate.off.other.1', 'sv.geo.gate.off.other.2', 'sv.geo.gate.off.other.3'];
  }
}

export function recoveryFor(browser: BrowserFamily, osLevel: boolean): string[] {
  if (osLevel) return ['sv.perm.rec.os.1', 'sv.perm.rec.os.2', 'sv.perm.rec.os.3'];
  switch (browser) {
    case 'chrome':
    case 'edge':
    case 'samsung':
      return ['sv.perm.rec.chrome.1', 'sv.perm.rec.chrome.2', 'sv.perm.rec.chrome.3'];
    case 'firefox':
      return ['sv.perm.rec.firefox.1', 'sv.perm.rec.firefox.2', 'sv.perm.rec.firefox.3'];
    case 'safari':
      return ['sv.perm.rec.safari.1', 'sv.perm.rec.safari.2', 'sv.perm.rec.safari.3'];
    default:
      return ['sv.perm.rec.generic.1', 'sv.perm.rec.generic.2', 'sv.perm.rec.generic.3'];
  }
}

export async function queryPermissionState(
  name: 'camera' | 'microphone' | 'geolocation',
): Promise<PermissionState | null> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return null;
  try {
    const status = await navigator.permissions.query({ name: name as PermissionName });
    return status.state;
  } catch {
    // Firefox historically rejects camera/microphone queries — treat as unknown.
    return null;
  }
}
